import React from 'react';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';

import { PreviewCycleResponse } from 'utils/api/modules/schedules/schedules.types';
import { formatMomentInZone } from 'utils/formatters/timezone';

import {
  PreviewContainer,
  PreviewDates,
  PreviewError,
  PreviewHeader,
  PreviewIndex,
  PreviewList,
  PreviewRefreshIndicator,
  PreviewRow,
  PreviewSpinner,
} from '../../styles';

interface RecurrencePreviewProps {
  cycles: PreviewCycleResponse[] | null;
  isLoading: boolean;
  error: string | null;
  zone?: string;
}

const formatCycle = (start: string, due: string, zone?: string): string => {
  if (zone) {
    const sameDay = dayjs(start).tz(zone).format('YYYY-MM-DD') === dayjs(due).tz(zone).format('YYYY-MM-DD');
    const format = sameDay ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
    return `${formatMomentInZone(start, zone, format)} → ${formatMomentInZone(due, zone, format)}`;
  }
  const startDay = dayjs(start);
  const dueDay = dayjs(due);
  const format = startDay.isSame(dueDay, 'day') ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
  return `${startDay.format(format)} → ${dueDay.format(format)}`;
};

const RecurrencePreview: React.FC<RecurrencePreviewProps> = ({ cycles, isLoading, error, zone }) => {
  const hasCycles = cycles !== null && cycles.length > 0;

  // No cycles yet — show first-load states (spinner / error / nothing).
  if (!hasCycles) {
    if (isLoading) {
      return (
        <PreviewContainer>
          <PreviewSpinner>
            <CircularProgress size={14} />
            <span>Calculating next audits…</span>
          </PreviewSpinner>
        </PreviewContainer>
      );
    }
    if (error) {
      return (
        <PreviewContainer>
          <PreviewError>{error}</PreviewError>
        </PreviewContainer>
      );
    }
    return null;
  }

  // Cycles exist — keep them visible during refetch so the container doesn't collapse
  // and the page below doesn't jump. Inline spinner signals that a refresh is in flight.
  return (
    <PreviewContainer>
      <PreviewHeader>
        Next {cycles!.length} audits
        {isLoading && (
          <PreviewRefreshIndicator>
            <CircularProgress size={10} thickness={5} />
          </PreviewRefreshIndicator>
        )}
      </PreviewHeader>
      <PreviewList>
        {cycles!.map((cycle, index) => (
          <PreviewRow key={`${cycle.start}-${cycle.due}`}>
            <PreviewIndex>{index + 1}.</PreviewIndex>
            <PreviewDates>{formatCycle(cycle.start, cycle.due, zone)}</PreviewDates>
          </PreviewRow>
        ))}
      </PreviewList>
    </PreviewContainer>
  );
};

export default RecurrencePreview;
